import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { ToastrService } from 'ngx-toastr';
import { GenericFormHandlerService } from '../shared/generic-crud.service';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { VentasService } from '../services/ventas.service';
import { ProductosService } from '../services/productos.service';
import moment from 'moment';
import 'moment/locale/es';
import { ReportsService } from '../services/reports.service';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-report-generator',
  templateUrl: './report-generator.component.html',
  styleUrls: ['./report-generator.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ]
})
export class ReportGeneratorComponent extends GenericFormHandlerService implements OnInit {
  reportForm: FormGroup;
  loading = false;
  dataLoaded = false;
  salesSummary: any = null;

  periodTitle = '';
  periodSubtitle = '';

  @ViewChild('dayPicker') dayPicker!: MatDatepicker<Date>;

  // Opciones de vista de reporte
  viewOptions = [
    { value: 'day', label: 'Hoy' },
    { value: 'week', label: 'Semanal' },
    { value: 'month', label: 'Mensual' },
    { value: 'custom', label: 'Personalizado' }
  ];

  // Datos para tablas y gráficos
  salesData: any[] = [];
  topProducts: any[] = [];
  salesByDate: any[] = [];

  // Configuración de gráficos
  public barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Fechas'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Ventas ($)'
        },
        beginAtZero: true
      }
    },
    plugins: {
      legend: {
        display: true,
      },
      tooltip: {
        callbacks: {
          label: (context) => `$${context.raw.toLocaleString()}`
        }
      }
    }
  };

  public barChartType: ChartType = 'bar';
  public barChartData: ChartData<'bar'>;

  public pieChartOptions: ChartConfiguration['options'];
  public pieChartType: ChartType = 'pie';
  public pieChartData: ChartData<'pie'>;

  // Columnas para tablas
  displayedColumnsSales = ['fecha', 'ventasTotales', 'cantidadVentas', 'productosVendidos'];
  displayedColumnsTopProducts = ['posicion', 'nombre', 'cantidadVendida', 'totalVendido'];

  // Datos por método de pago
  salesByMetodoPago: { metodoPago: string; total: number; cantidad: number }[] = [];
  metodoPagoLabels: Record<string, string> = {
    efectivo: 'Efectivo',
    tarjeta: 'Tarjeta',
    transferencia: 'Transferencia',
  };
  metodoPagoColors: Record<string, string> = {
    efectivo: '#4CAF50',
    tarjeta: '#2196F3',
    transferencia: '#FF9800',
  };

  private readonly piePalette = [
    { base: 'rgba(99, 102, 241, 0.85)', hover: 'rgba(79, 70, 229, 0.95)' },
    { base: 'rgba(14, 165, 233, 0.85)', hover: 'rgba(2, 132, 199, 0.95)' },
    { base: 'rgba(34, 197, 94, 0.85)', hover: 'rgba(22, 163, 74, 0.95)' },
    { base: 'rgba(251, 191, 36, 0.85)', hover: 'rgba(245, 158, 11, 0.95)' },
    { base: 'rgba(239, 68, 68, 0.85)', hover: 'rgba(220, 38, 38, 0.95)' },
    { base: 'rgba(168, 85, 247, 0.85)', hover: 'rgba(147, 51, 234, 0.95)' },
    { base: 'rgba(45, 212, 191, 0.85)', hover: 'rgba(13, 148, 136, 0.95)' },
    { base: 'rgba(165, 180, 252, 0.85)', hover: 'rgba(129, 140, 248, 0.95)' },
    { base: 'rgba(248, 113, 113, 0.85)', hover: 'rgba(239, 68, 68, 0.95)' },
    { base: 'rgba(59, 130, 246, 0.85)', hover: 'rgba(37, 99, 235, 0.95)' }
  ];

  constructor(
    private fb: FormBuilder,
    protected reportsService: ReportsService,
    protected toastr: ToastrService
  ) {
    super(reportsService, toastr);
    this.createForm();
    this.resetCharts();
    this.pieChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            font: {
              size: 13
            },
            padding: 18
          }
        },
        tooltip: {
          bodyFont: {
            size: 13
          },
          callbacks: {
            label: (context) => {
              const label = context.label || '';
              const value = Number(context.raw) || 0;
              const dataset = context.dataset.data as (number | null | undefined)[];
              const total = dataset
                .map(v => Number(v) || 0)
                .reduce((acc, current) => acc + current, 0);
              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
              const product = this.topProducts?.[context.dataIndex];
              const units = product?.cantidadVendida ?? 0;
              const currency = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
              return `${label}: ${currency} · ${units} uds · ${percentage}%`;
            }
          }
        }
      }
    };
  }

  ngOnInit(): void {
    moment.locale('es');
    this.setDefaultDateRange();
    this.updatePeriodDisplay();
    void this.loadReports();
  }

  private createForm(): void {
    this.reportForm = this.fb.group({
      viewType: ['day'],
      startDate: [null],
      endDate: [null]
    });

    this.reportForm.get('viewType')?.valueChanges.subscribe(value => {
      this.setDefaultDateRange(value);
      this.updatePeriodDisplay();
      void this.loadReports();
    });

    this.reportForm.get('startDate')?.valueChanges.subscribe(() => {
      if (this.reportForm.get('viewType')?.value === 'custom') {
        this.updatePeriodDisplay();
      }
    });

    this.reportForm.get('endDate')?.valueChanges.subscribe(() => {
      if (this.reportForm.get('viewType')?.value === 'custom') {
        this.updatePeriodDisplay();
      }
    });
  }

  private setDefaultDateRange(viewType: string = 'day'): void {
    const viewTypeValue = viewType || this.reportForm?.get('viewType')?.value || 'day';
    const today = moment();
    let startDate, endDate;

    switch (viewTypeValue) {
      case 'month':
        startDate = moment().startOf('month');
        endDate = moment().endOf('month');
        break;
      case 'custom':
        // No establecemos fechas por defecto para rango personalizado
        return;
      case 'day':
        startDate = moment().startOf('day');
        endDate = moment().endOf('day');
        break;
      case 'week':
      default:
        startDate = moment().startOf('isoWeek');
        endDate = moment().endOf('isoWeek');
    }

    this.reportForm.patchValue({
      startDate: startDate.toDate(),
      endDate: endDate.toDate()
    }, { emitEvent: false });
  }

  async loadReports(): Promise<void> {
    if (!this.reportForm.valid) {
      this.toastr.warning('Por favor complete las fechas correctamente');
      return;
    }

    const { startDate, endDate } = this.reportForm.value;
    if (!startDate || !endDate) return;

    this.loading = true;
    this.dataLoaded = false;
    this.salesSummary = null;
    this.salesData = [];
    this.topProducts = [];
    this.salesByDate = [];
    this.salesByMetodoPago = [];
    this.resetCharts();

    // Convertir fechas a formato ISO
    const start = moment(startDate).startOf('day').toISOString();
    const end = moment(endDate).endOf('day').toISOString();

    try {
      await Promise.all([
        this.loadSalesReport(start, end),
        this.loadTopProducts(start, end),
        this.loadSalesByDate(start, end),
        this.loadSalesByMetodoPago(start, end)
      ]);
    } finally {
      this.loading = false;
      this.dataLoaded = true;
      this.updatePeriodDisplay();
    }
  }

  changePeriod(direction: 'prev' | 'next'): void {
    const viewType = this.reportForm.get('viewType')?.value ?? 'day';
    let start = this.reportForm.get('startDate')?.value ? moment(this.reportForm.get('startDate')?.value) : moment();
    let end = this.reportForm.get('endDate')?.value ? moment(this.reportForm.get('endDate')?.value) : moment();

    const step = direction === 'next' ? 1 : -1;

    switch (viewType) {
      case 'day':
        start = start.add(step, 'day').startOf('day');
        end = start.clone().endOf('day');
        break;
      case 'week':
        start = start.add(step, 'week').startOf('isoWeek');
        end = start.clone().endOf('isoWeek');
        break;
      case 'month':
        start = start.add(step, 'month').startOf('month');
        end = start.clone().endOf('month');
        break;
      case 'custom':
        const currentStart = this.reportForm.get('startDate')?.value ? moment(this.reportForm.get('startDate')?.value).startOf('day') : moment().startOf('day');
        const currentEnd = this.reportForm.get('endDate')?.value ? moment(this.reportForm.get('endDate')?.value).endOf('day') : moment().endOf('day');
        const diffDays = currentEnd.diff(currentStart, 'days');
        start = currentStart.add(step * (diffDays + 1), 'days');
        end = start.clone().add(diffDays, 'days').endOf('day');
        break;
      default:
        start = start.add(step, 'day').startOf('day');
        end = start.clone().endOf('day');
        break;
    }

    this.reportForm.patchValue(
      {
        startDate: start.toDate(),
        endDate: end.toDate()
      },
      { emitEvent: false }
    );

    this.updatePeriodDisplay();
    void this.loadReports();
  }

  private updatePeriodDisplay(): void {
    const viewType = this.reportForm.get('viewType')?.value ?? 'day';
    const startValue = this.reportForm.get('startDate')?.value;
    const endValue = this.reportForm.get('endDate')?.value;

    if (!startValue || !endValue) {
      this.periodTitle = this.getViewTitle(viewType);
      this.periodSubtitle = 'Selecciona un rango de fechas para ver el reporte.';
      return;
    }

    const start = moment(startValue);
    const end = moment(endValue);

    this.periodTitle = this.getViewTitle(viewType);
    this.periodSubtitle = this.buildPeriodSubtitle(viewType, start, end);
  }

  private getViewTitle(viewType: string): string {
    switch (viewType) {
      case 'day':
        return 'Vista del día';
      case 'week':
        return 'Semana seleccionada';
      case 'month':
        return 'Mes seleccionado';
      case 'custom':
        return 'Rango personalizado';
      default:
        return 'Periodo seleccionado';
    }
  }

  private buildPeriodSubtitle(viewType: string, start: moment.Moment, end: moment.Moment): string {
    // Asegurar que el locale esté en español
    const startEs = start.locale('es');
    const endEs = end.locale('es');
    
    const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
    const formatDay = (date: moment.Moment) => {
      const formatted = date.format('dddd D [de] MMMM YYYY');
      return capitalize(formatted);
    };

    if (viewType === 'day' || startEs.isSame(endEs, 'day')) {
      return formatDay(startEs);
    }

    if (viewType === 'week') {
      return `Del ${formatDay(startEs)} al ${formatDay(endEs)}`;
    }

    if (viewType === 'month') {
      return capitalize(startEs.format('MMMM YYYY'));
    }

    return `Del ${formatDay(startEs)} al ${formatDay(endEs)}`;
  }

  private async loadSalesReport(start: string, end: string): Promise<void> {
    try {
      const params = { start, end };
      const response = await this.reportsService.post(params, 'resumen').toPromise();
      const summary = Array.isArray(response) ? response[0] : response;
      this.salesSummary = summary
        ? {
            totalVentas: Number(summary.totalVentas ?? 0),
            cantidadVentas: Number(summary.cantidadVentas ?? 0),
            productosVendidos: Number(summary.productosVendidos ?? 0)
          }
        : null;
      this.salesData = this.salesSummary ? [this.salesSummary] : [];
    } catch (error) {
      this.toastr.error('Error al cargar el reporte de ventas');
      console.error(error);
    }
  }

  private async loadTopProducts(start: string, end: string): Promise<void> {
    try {
      const params = { start, end, limit: 10 };
      const data = await this.reportsService.post(params, 'top-productos').toPromise();
      this.topProducts = Array.isArray(data)
        ? data.map((item, index) => ({
            ...item,
            totalVendido: Number(item.totalVendido ?? 0),
            cantidadVendida: Number(item.cantidadVendida ?? 0),
            posicion: index + 1
          }))
        : [];
      this.updatePieChart();
    } catch (error) {
      this.toastr.error('Error al cargar los productos más vendidos');
      console.error(error);
    }
  }

  private async loadSalesByMetodoPago(start: string, end: string): Promise<void> {
    try {
      const data = await this.reportsService.obtenerPorMetodoPago(start, end).toPromise();
      this.salesByMetodoPago = Array.isArray(data)
        ? data.map(item => ({
            metodoPago: item.metodoPago,
            total: Number(item.total ?? 0),
            cantidad: Number(item.cantidad ?? 0),
          }))
        : [];
    } catch {
      this.salesByMetodoPago = [];
    }
  }

  private async loadSalesByDate(start: string, end: string): Promise<void> {
    try {
      const params = { start, end, groupBy: 'day' };
      const data = await this.reportsService.post(params, 'por-fecha').toPromise();
      this.salesByDate = Array.isArray(data)
        ? data.map(item => ({
            ...item,
            ventasTotales: Number(item.ventasTotales ?? 0),
            cantidadVentas: Number(item.cantidadVentas ?? 0),
            productosVendidos: Number(item.productosVendidos ?? 0)
          }))
        : [];
      this.updateBarChart();
    } catch (error) {
      this.toastr.error('Error al cargar las ventas por fecha');
      console.error(error);
    }
  }

  private updateBarChart(): void {
    const labels = this.salesByDate.map(item => moment(item.fecha).format('DD/MM'));
    const data = this.salesByDate.map(item => item.ventasTotales);
    this.barChartData = {
      labels,
      datasets: [
        {
          label: 'Ventas por día',
          data,
          backgroundColor: 'rgba(54, 162, 235, 0.45)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    };
  }

  private updatePieChart(): void {
    if (!this.topProducts.length) {
      this.pieChartData = {
        labels: [],
        datasets: [{ data: [], backgroundColor: [], hoverBackgroundColor: [] }]
      };
      return;
    }

    const labels = this.topProducts.map(item => item.nombre);
    const data = this.topProducts.map(item => item.totalVendido);
    const palette = this.piePalette.slice(0, data.length);

    this.pieChartData = {
      labels,
      datasets: [
        {
          data,
          backgroundColor: palette.map(color => color.base),
          hoverBackgroundColor: palette.map(color => color.hover)
        }
      ]
    };
  }

  private resetCharts(): void {
    this.barChartData = {
      labels: [],
      datasets: [
        {
          data: [],
          label: 'Ventas por día',
          backgroundColor: 'rgba(54, 162, 235, 0.45)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    };

    this.pieChartData = {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [],
          hoverBackgroundColor: []
        }
      ]
    };
  }

  get hasData(): boolean {
    return !!this.salesSummary || this.topProducts.length > 0 || this.salesByDate.length > 0;
  }

  get totalVentas(): number {
    return this.salesSummary?.totalVentas ?? 0;
  }

  get totalProductosVendidos(): number {
    return this.salesSummary?.productosVendidos ?? 0;
  }

  get cantidadVentas(): number {
    return this.salesSummary?.cantidadVentas ?? 0;
  }

  openDayPicker(): void {
    const viewType = this.reportForm.get('viewType')?.value;
    if (viewType === 'day' && this.dayPicker) {
      this.dayPicker.open();
    }
  }

  onDaySelected(event: MatDatepickerInputEvent<Date>): void {
    const date = event.value;
    if (date) {
      const selectedDate = moment(date);
      this.reportForm.patchValue({
        startDate: selectedDate.startOf('day').toDate(),
        endDate: selectedDate.endOf('day').toDate()
      }, { emitEvent: false });
      this.updatePeriodDisplay();
      void this.loadReports();
    }
  }

  get isDayView(): boolean {
    return this.reportForm.get('viewType')?.value === 'day';
  }
}
